import { VOBFormData } from '../types';
import {
    ACTIVE_POLICY_EFFECTIVE_TO_ERROR_MESSAGE,
    isActivePolicyEffectiveToBeforeToday,
    VOB_SAVE_VALIDATION_ISSUES_PREFIX,
} from './active-policy-effective-to-validation';

export interface VobLevelFieldErrors {
    deductibleTotalError?: boolean;
    deductibleRemainingError?: boolean;
    oopmTotalError?: boolean;
    oopmRemainingError?: boolean;
    copayError?: boolean;
    maxDaysError?: boolean;
    coinsuranceError?: boolean;
    deductibleTotalErrorMessage?: string;
    deductibleRemainingErrorMessage?: string;
    oopmTotalErrorMessage?: string;
    oopmRemainingErrorMessage?: string;
    copayErrorMessage?: string;
    maxDaysErrorMessage?: string;
    coinsuranceErrorMessage?: string;
}

export interface VobFormValidationInput {
    selectedFacility: number | '';
    skipFacilityError?: boolean;
    effectiveFromDate: Date | null;
    effectiveToDate: Date | null;
    activePolicy: boolean;
    formData: VOBFormData;
    levelsOfCare: Array<{ vobClassificationId?: number }> | null | undefined;
    savedLOCDataMap: Map<number, any>;
    networkType: string;
    memberType: string;
}

export interface VobFormFieldErrors {
    facilityError: boolean;
    dateErrors: {
        effectiveFrom: boolean;
        effectiveTo: boolean;
    };
    deductibleError: boolean;
    remainingError: boolean;
    oopmError: boolean;
    oopmRemainingError: boolean;
    deductibleErrorMessage: string;
    remainingErrorMessage: string;
    oopmErrorMessage: string;
    oopmRemainingErrorMessage: string;
    copayError: boolean;
    maxDaysError: boolean;
    coinsuranceError: boolean;
    copayErrorMessage: string;
    maxDaysErrorMessage: string;
    coinsuranceErrorMessage: string;
    levelErrors: Map<number, VobLevelFieldErrors>;
    hasError: boolean;
    hasNegative: boolean;
    hasCoinsuranceOver100: boolean;
    hasLevelErrors: boolean;
    errorMessage: string;
}

interface TotalRemainingPairResult {
    totalError: boolean;
    remainingError: boolean;
    totalErrorMessage: string;
    remainingErrorMessage: string;
    hasNegative: boolean;
    hasPairError: boolean;
    errorMessageSuffix: string;
}

function parseFilledNumber(raw: string | undefined): number {
    return parseFloat(raw || '');
}

function isFilled(raw: string | undefined): boolean {
    return !!raw;
}

function validateTotalRemainingPair(
    totalRaw: string | undefined,
    remainingRaw: string | undefined,
    comparisonMessage: string,
    remainingRequiredMessage: string,
    totalRequiredMessage: string
): TotalRemainingPairResult {
    const total = parseFilledNumber(totalRaw);
    const remaining = parseFilledNumber(remainingRaw);
    let totalError = false;
    let remainingError = false;
    let totalErrorMessage = '';
    let remainingErrorMessage = '';
    let hasNegative = false;
    let hasPairError = false;
    let errorMessageSuffix = '';

    if (!isNaN(total) && totalRaw !== '' && total < 0) {
        totalError = true;
        totalErrorMessage = 'Total cannot be negative';
        hasNegative = true;
    }
    if (!isNaN(remaining) && remainingRaw !== '' && remaining < 0) {
        remainingError = true;
        remainingErrorMessage = 'Remaining cannot be negative';
        hasNegative = true;
    }

    if (
        !isNaN(total) &&
        !isNaN(remaining) &&
        totalRaw !== '' &&
        remainingRaw !== '' &&
        total < remaining
    ) {
        totalError = true;
        remainingError = true;
        totalErrorMessage = comparisonMessage;
        remainingErrorMessage = comparisonMessage;
        hasPairError = true;
        errorMessageSuffix += `${comparisonMessage}; `;
    } else if (isFilled(totalRaw) && !isFilled(remainingRaw)) {
        remainingError = true;
        remainingErrorMessage = remainingRequiredMessage;
        hasPairError = true;
        errorMessageSuffix += `${remainingRequiredMessage}; `;
    } else if (!isFilled(totalRaw) && isFilled(remainingRaw)) {
        totalError = true;
        totalErrorMessage = totalRequiredMessage;
        hasPairError = true;
        errorMessageSuffix += `${totalRequiredMessage}; `;
    }

    return {
        totalError,
        remainingError,
        totalErrorMessage,
        remainingErrorMessage,
        hasNegative,
        hasPairError,
        errorMessageSuffix,
    };
}

function validateSignedNumber(
    raw: string | undefined,
    negativeMessage: string,
    over100Message?: string
): { error: boolean; errorMessage: string; hasNegative: boolean; hasOver100: boolean } {
    const value = parseFilledNumber(raw);
    if (!isNaN(value) && raw !== '' && value < 0) {
        return {
            error: true,
            errorMessage: negativeMessage,
            hasNegative: true,
            hasOver100: false,
        };
    }
    if (over100Message && !isNaN(value) && raw !== '' && value > 100) {
        return {
            error: true,
            errorMessage: over100Message,
            hasNegative: false,
            hasOver100: true,
        };
    }
    return { error: false, errorMessage: '', hasNegative: false, hasOver100: false };
}

function validateLevelOfCareFields(
    levelsOfCare: VobFormValidationInput['levelsOfCare'],
    savedLOCDataMap: VobFormValidationInput['savedLOCDataMap'],
    networkType: string,
    memberType: string
): { levelErrors: Map<number, VobLevelFieldErrors>; hasLevelErrors: boolean } {
    const levelErrors = new Map<number, VobLevelFieldErrors>();
    let hasLevelErrors = false;

    if (!levelsOfCare || levelsOfCare.length === 0) {
        return { levelErrors, hasLevelErrors };
    }

    const combinationKey = `${networkType}_${memberType}`;

    levelsOfCare.forEach((level) => {
        const vobClassificationId = level.vobClassificationId;
        if (!vobClassificationId) return;

        const levelData = savedLOCDataMap.get(vobClassificationId);
        const savedData =
            levelData instanceof Map ? levelData.get(combinationKey) || {} : {};
        const levelError: VobLevelFieldErrors = {};

        const deductiblePair = validateTotalRemainingPair(
            savedData.total,
            savedData.remaining,
            'Deductible Total must be greater than or equal to Remaining',
            'Remaining is required when Deductible is filled',
            'Deductible is required when Remaining is filled'
        );
        if (deductiblePair.totalError) {
            levelError.deductibleTotalError = true;
            levelError.deductibleTotalErrorMessage = deductiblePair.totalErrorMessage;
            hasLevelErrors = true;
        }
        if (deductiblePair.remainingError) {
            levelError.deductibleRemainingError = true;
            levelError.deductibleRemainingErrorMessage = deductiblePair.remainingErrorMessage;
            hasLevelErrors = true;
        }

        const oopmPair = validateTotalRemainingPair(
            savedData.oopmTotal,
            savedData.oopmRemaining,
            'Out of Pocket Maximum Total must be greater than or equal to Remaining',
            'OOP Remaining is required when OOPM is filled',
            'OOPM is required when OOP Remaining is filled'
        );
        if (oopmPair.totalError) {
            levelError.oopmTotalError = true;
            levelError.oopmTotalErrorMessage = oopmPair.totalErrorMessage;
            hasLevelErrors = true;
        }
        if (oopmPair.remainingError) {
            levelError.oopmRemainingError = true;
            levelError.oopmRemainingErrorMessage = oopmPair.remainingErrorMessage;
            hasLevelErrors = true;
        }

        const copay = validateSignedNumber(savedData.perDayVisit, 'Co-pay cannot be negative');
        if (copay.error) {
            levelError.copayError = true;
            levelError.copayErrorMessage = copay.errorMessage;
            hasLevelErrors = true;
        }

        const maxDays = validateSignedNumber(savedData.maxDays, 'Max Days cannot be negative');
        if (maxDays.error) {
            levelError.maxDaysError = true;
            levelError.maxDaysErrorMessage = maxDays.errorMessage;
            hasLevelErrors = true;
        }

        const coinsurance = validateSignedNumber(
            savedData.patient,
            'Coinsurance cannot be negative',
            'Patient % cannot be greater than 100'
        );
        if (coinsurance.error) {
            levelError.coinsuranceError = true;
            levelError.coinsuranceErrorMessage = coinsurance.errorMessage;
            hasLevelErrors = true;
        }

        if (Object.keys(levelError).length > 0) {
            levelErrors.set(vobClassificationId, levelError);
        }
    });

    return { levelErrors, hasLevelErrors };
}

export function validateVobFormFields(
    input: VobFormValidationInput
): VobFormFieldErrors {
    let hasError = false;
    let errorMessage = VOB_SAVE_VALIDATION_ISSUES_PREFIX;
    const dateErrors = {
        effectiveFrom: false,
        effectiveTo: false,
    };
    let facilityError = false;

    if (!input.skipFacilityError && !input.selectedFacility) {
        hasError = true;
        facilityError = true;
        errorMessage += 'Facility is required; ';
    }

    if (!input.effectiveFromDate) {
        hasError = true;
        dateErrors.effectiveFrom = true;
        errorMessage += 'Effective From is required; ';
    }

    if (!input.effectiveToDate) {
        hasError = true;
        dateErrors.effectiveTo = true;
        errorMessage += 'Effective To is required; ';
    } else if (
        isActivePolicyEffectiveToBeforeToday(input.activePolicy, input.effectiveToDate)
    ) {
        hasError = true;
        dateErrors.effectiveTo = true;
        errorMessage += `${ACTIVE_POLICY_EFFECTIVE_TO_ERROR_MESSAGE}; `;
    }

    const deductiblePair = validateTotalRemainingPair(
        input.formData.deductible,
        input.formData.remaining,
        'Deductible Total must be greater than or equal to Remaining',
        'Remaining is required when Deductible is filled',
        'Deductible is required when Remaining is filled'
    );
    const oopmPair = validateTotalRemainingPair(
        input.formData.oopm,
        input.formData.oopmRemaining,
        'Out of Pocket Maximum Total must be greater than or equal to Remaining',
        'OOP Remaining is required when OOPM is filled',
        'OOPM is required when OOP Remaining is filled'
    );
    const copay = validateSignedNumber(input.formData.copay, 'Co-pay cannot be negative');
    const maxDays = validateSignedNumber(input.formData.maxDays, 'Max Days cannot be negative');
    const coinsurance = validateSignedNumber(
        input.formData.coinsurance,
        'Coinsurance cannot be negative',
        'Patient % cannot be greater than 100'
    );

    if (deductiblePair.hasPairError) {
        hasError = true;
        errorMessage += deductiblePair.errorMessageSuffix;
    }
    if (oopmPair.hasPairError) {
        hasError = true;
        errorMessage += oopmPair.errorMessageSuffix;
    }

    const hasNegative =
        deductiblePair.hasNegative ||
        oopmPair.hasNegative ||
        copay.hasNegative ||
        maxDays.hasNegative ||
        coinsurance.hasNegative;
    const hasCoinsuranceOver100 = coinsurance.hasOver100;

    const { levelErrors, hasLevelErrors } = validateLevelOfCareFields(
        input.levelsOfCare,
        input.savedLOCDataMap,
        input.networkType,
        input.memberType
    );

    return {
        facilityError,
        dateErrors,
        deductibleError: deductiblePair.totalError,
        remainingError: deductiblePair.remainingError,
        oopmError: oopmPair.totalError,
        oopmRemainingError: oopmPair.remainingError,
        deductibleErrorMessage: deductiblePair.totalErrorMessage,
        remainingErrorMessage: deductiblePair.remainingErrorMessage,
        oopmErrorMessage: oopmPair.totalErrorMessage,
        oopmRemainingErrorMessage: oopmPair.remainingErrorMessage,
        copayError: copay.error,
        maxDaysError: maxDays.error,
        coinsuranceError: coinsurance.error,
        copayErrorMessage: copay.errorMessage,
        maxDaysErrorMessage: maxDays.errorMessage,
        coinsuranceErrorMessage: coinsurance.errorMessage,
        levelErrors,
        hasError,
        hasNegative,
        hasCoinsuranceOver100,
        hasLevelErrors,
        errorMessage,
    };
}

export function emptyVobFormData(): VOBFormData {
    return {
        deductible: '',
        remaining: '',
        oopm: '',
        oopmRemaining: '',
        copay: '',
        maxDays: '',
        coinsurance: '',
        notes: '',
        isDedIncOOPM: true,
    };
}
